import { Flex, SortOrder, Table } from '@approvalmax/ui/src/components';
import { useAtom } from 'jotai';
import { selectors } from 'modules/common';
import { State } from 'modules/data';
import { useMyTimeZone } from 'modules/utils';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useGetCompanyDelegations } from 'shared/data/webApp/v2';

import { getTableColumns } from './UserList.config';
import { checkboxCoverColumn } from './UserList.constants';
import { getListData } from './UserList.helpers';
import { userListDataState } from './UserList.states';
import { ColumnId, SortState, UserListProps } from './UserList.types';

export const UserList = memo<UserListProps>((props) => {
    const { effectiveFilterText, checkedItemIds, setCheckedItemIds } = props;

    const company = useSelector(selectors.navigation.getActiveCompany);

    const { data: delegations } = useGetCompanyDelegations({
        path: { companyId: company.id },
    });
    const [userListData, setUserListData] = useAtom(userListDataState);

    const [sortState, setSortState] = useState<SortState>({
        columnId: undefined,
        sortOrder: undefined,
    });

    const companyTeam = useSelector((state: State) => selectors.company.getCompanyTeam(state, company));

    const companyDelegates = useMemo(() => {
        const delegates = delegations?.delegates || [];

        return new Map(delegates.map((delegate) => [delegate.fromUser.userId, delegate]));
    }, [delegations?.delegates]);
    const myTimeZone = useMyTimeZone();

    const columnDefinitions = useMemo(() => {
        const tableColumns = getTableColumns(company);

        const { columnId, sortOrder } = sortState;

        return tableColumns.map((colDef) => {
            if (colDef.id === columnId) {
                return { ...colDef, sortOrder };
            }

            if (colDef.sortOrder) {
                return { ...colDef, sortOrder: undefined };
            }

            return colDef;
        });
    }, [company, sortState]);

    const handleGetListData = useCallback(() => {
        const sortColumn = columnDefinitions.find((colDef) => Boolean(colDef.sortOrder));

        return getListData({}, companyTeam, effectiveFilterText, company, sortColumn);
    }, [columnDefinitions, company, companyTeam, effectiveFilterText]);

    const onSort = useCallback((columnId: string, newSortOrder: SortOrder) => {
        setSortState({
            columnId,
            sortOrder: newSortOrder,
        });
    }, []);

    const listData = handleGetListData();

    const enrichedListData = useMemo(
        () =>
            listData.map((row) => {
                const delegate = row.databaseId ? companyDelegates.get(row.databaseId) : null;

                return {
                    ...row,
                    delegateUserId: delegate?.toUser?.email,
                    [ColumnId.Delegate]: delegate?.toUser?.email,
                    [ColumnId.DelegateTimeZone]: myTimeZone,
                    [ColumnId.DelegateDateStart]: delegate?.dateFrom,
                    [ColumnId.DelegateDateEnd]: delegate?.dateTo,
                };
            }),
        [companyDelegates, listData, myTimeZone]
    );

    useEffect(() => {
        setUserListData(enrichedListData);
    }, [enrichedListData, setUserListData]);

    const onTableCheckedItemsChange = useCallback(
        (checkedItemIds: string[]) => {
            setCheckedItemIds(checkedItemIds);
        },
        [setCheckedItemIds]
    );

    return (
        <Flex direction='column' height='100%'>
            <Table
                className='fs-mask'
                checkboxColumnCover={checkboxCoverColumn}
                onSort={onSort}
                checkedItems={checkedItemIds}
                onCheckedItemsChange={onTableCheckedItemsChange}
                columns={columnDefinitions}
                data={userListData}
                headerColor='white100'
                virtualized
                fontSize='small'
            />
        </Flex>
    );
});

UserList.displayName = 'UserList';
